import { css } from '@/styled-system/css';

const THUMBNAIL_SIZE = '2500';
const ROW_LABEL_COLUMN_WIDTH = '2300';
// Inlined `{sizes.2300}` (matching ROW_LABEL_COLUMN_WIDTH) so Panda's static
// extractor resolves the token to its hashed var in prod builds.
const ARROW_RAIL_INLINE_START = `calc({sizes.2300} + {spacing.tightest})`;

export const container = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'default',
  minWidth: 0,
  maxWidth: '100%',
});

export const tableWrapper = css({
  position: 'relative',
  width: '100%',
  minWidth: 0,
  maxWidth: '100%',
});

export const scrollContainer = css({
  minWidth: 0,
  overflowY: 'hidden',
  WebkitOverflowScrolling: 'touch',
  paddingRight: 'layout.looser',
  paddingBottom: 'tighter',
  scrollbarWidth: 'none',
  backgroundColor: 'transparent',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  '@media (prefers-reduced-motion: reduce)': {
    scrollBehavior: 'auto',
  },
});

export const table = css({
  tableLayout: 'fixed',
  width: 'max-content',
  minWidth: '100%',
  backgroundColor: 'transparent',
  // Keep Constellation from painting a dark panel over the module background in dark mode.
  '& [data-c11n-component="Table.Header"], & [data-c11n-component="Table.Body"], & [data-c11n-component="Table.Row"], & [data-c11n-component="Table.HeaderCell"], & [data-c11n-component="Table.Cell"]':
    {
      backgroundColor: 'transparent',
    },
});

export const rowLabelColumn = css({
  width: ROW_LABEL_COLUMN_WIDTH,
});

export const listingColumn = css({
  width: '3600',
});

export const rowLabelHeader = css({
  padding: 0,
  position: 'sticky',
  left: 0,
  zIndex: 1,
});

export const listingHeader = css({
  paddingTop: 0,
  paddingRight: 'tighter',
  paddingBottom: 0,
  paddingLeft: 'tighter',
  verticalAlign: 'top',
  fontWeight: 'regular',
});

export const currentListingHeader = css({
  paddingTop: 0,
  paddingRight: 'tighter',
  paddingBottom: 0,
  paddingLeft: 'default',
  verticalAlign: 'top',
  fontWeight: 'regular',
});

export const columnHeader = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'tighter',
  paddingBottom: 'tighter',
  textDecoration: 'none',
  color: 'inherit',
  cursor: 'pointer',
});

export const columnHeaderCurrent = css({
  cursor: 'default',
});

export const listingImageWrapper = css({
  width: THUMBNAIL_SIZE,
  height: THUMBNAIL_SIZE,
  borderRadius: 'sm',
  overflow: 'hidden',
  flexShrink: 0,
});

export const listingImage = css({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  backgroundColor: 'bg.softest',
});

export const noPhotoPlaceholder = css({
  width: THUMBNAIL_SIZE,
  height: THUMBNAIL_SIZE,
  borderRadius: 'sm',
  backgroundColor: 'bg.softest',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'text.subtle',
  fontSize: 'body-xs',
});

export const listingName = css({
  fontSize: 'body-sm',
  fontWeight: 'bold',
  color: 'text.accent.brand.hero',
  lineHeight: 'body-sm',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  overflowWrap: 'break-word',
  margin: 0,
});

export const currentListingText = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
});

export const currentListingLabel = css({
  fontSize: 'body-sm',
  fontWeight: 'bold',
  color: 'text.neutral',
  lineHeight: 'body-sm',
  margin: 0,
});

export const currentListingName = css({
  fontSize: 'body-sm',
  fontWeight: 'regular',
  color: 'text.subtle',
  lineHeight: 'body-sm',
  overflowWrap: 'break-word',
  margin: 0,
});

export const rowLabelCell = css({
  color: 'text.subtle',
  paddingLeft: 0,
  // Use && to match Constellation recipe specificity (it generates [&&]:text_left.[&&]:text_left)
  '&&': { textAlign: 'right' },
  borderRightWidth: 'default',
  borderRightStyle: 'solid',
  borderRightColor: 'border.neutral',
  whiteSpace: 'nowrap',
  position: 'sticky',
  left: 0,
  zIndex: 1,
});

export const valueCell = css({
  whiteSpace: 'normal',
  overflowWrap: 'break-word',
});

export const priceCell = css({
  fontWeight: 'bold',
});

export const totalPriceBadgeCell = css({
  whiteSpace: 'normal',
  paddingBottom: 'tighter',
});

export const firstBodyRow = css({
  paddingTop: 'default',
});

export const checkmarkIcon = css({
  color: 'text.accent.brand.hero',
});

export const arrowRail = css({
  position: 'absolute',
  top: '1000',
  insetInlineStart: ARROW_RAIL_INLINE_START,
  insetInlineEnd: 'tightest',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  minWidth: 0,
  overflow: 'hidden',
  pointerEvents: 'none',
  zIndex: 'zIndexStepOver',
});

export const arrowSlot = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 'action.sm',
  height: 'action.sm',
  flexShrink: 0,
});

export const arrowButton = css({
  pointerEvents: 'auto',
});

export const areaLinkContainer = css({
  display: 'flex',
  justifyContent: 'flex-end',
  paddingTop: 'tighter',
});

export const areaLink = css({
  fontSize: 'body-sm',
  fontWeight: 'regular',
  color: 'text.accent.brand.hero',
  textDecoration: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: 'tightest',
  _hover: {
    color: 'text.action.hero.hover',
    textDecoration: 'underline',
  },
});
